import { FC } from "react";
import { useSelector } from "react-redux";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiButtonTertiary,
  MuiGrid,
  MuiTypography,
} from "../../../../components";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import PermissionWrapper from "../../../../components/permissionWrapper/PermissionWrapper";
import { MuiInfoToolTip } from "../../../../components/tooltip/infoToolTip/MuiInfoToolTip";
import {
  EMPTY_VALUE_DASH,
  MODULE_PERMISSIONS,
} from "../../../../helpers/constants";
import { COMPONENT_IDS } from "../../../../helpers/constants/componentIds";
import { isEmpty, isNotEmpty, isRetailClient } from "../../../../helpers/utils";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { createClientSelector } from "../../../../redux/slices/client/createClientSelector";
import { keyClientDetailsSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CreateClientSemiHeaderWrapper } from "./CreateClientSemiHeader.style";

export type CreateClientSemiHeaderProps = {
  clientCode?: string;
  parentClient?: string;
  onTagParentClientClick?: () => void;
  onRemoveParentClientClick?: () => void;
  isBillingConfigurationSaved: boolean;
  isParentClientRemovalAllowed?: boolean;
  parentClientCode?: string;
  isEditable?: boolean;
  isAddButtonVisible?: boolean;
  clientName?: string;
};

export const CreateClientSemiHeader: FC<CreateClientSemiHeaderProps> = (
  props: CreateClientSemiHeaderProps,
) => {
  const { fetchLabel, fetchComponentTitle } = useSiteCore();
  const clientData = useSelector(createClientSelector);

  const {
    clientCode,
    parentClient,
    onTagParentClientClick,
    onRemoveParentClientClick,
    isBillingConfigurationSaved,
    isParentClientRemovalAllowed,
    parentClientCode,
    isEditable = true,
    isAddButtonVisible = true,
    clientName,
  } = props;

  const handleParentClientClick = () => {
    if (parentClient) {
      onRemoveParentClientClick?.();
    } else {
      onTagParentClientClick?.();
    }
  };

  const renderParentClientButton = () => {
    const isDisabled =
      !!parentClient &&
      !isParentClientRemovalAllowed &&
      isBillingConfigurationSaved;
    const buttonText = parentClient
      ? fetchLabel(
          keyClientDetailsSiteCoreKeys.keyClientDetailsHeader,
          keyClientDetailsSiteCoreKeys.removeParentClient,
        )
      : fetchLabel(
          keyClientDetailsSiteCoreKeys.keyClientDetailsHeader,
          keyClientDetailsSiteCoreKeys.tagParentClient,
        );

    return (
      <PermissionWrapper
        permission={[
          MODULE_PERMISSIONS.ADD_CLIENT_PARENT_CONTACTS,
          MODULE_PERMISSIONS.EDIT_CLIENT_PARENT_CONTACTS,
        ]}
      >
        <MuiBox className="header-parent-client-button-container">
          <MuiButtonTertiary
            id={
              parentClient
                ? COMPONENT_IDS.REMOVE_PARENT_BUTTON
                : COMPONENT_IDS.TAG_PARENT_BUTTON
            }
            onClick={handleParentClientClick}
            className={
              isDisabled ? "header-tag-parent disabled" : "header-tag-parent"
            }
            disabled={isDisabled}
          >
            {buttonText}
          </MuiButtonTertiary>
          {isDisabled && (
            <MuiInfoToolTip
              message={fetchLabel(
                keyClientDetailsSiteCoreKeys.keyClientDetailsHeader,
                keyClientDetailsSiteCoreKeys.removeTheParentClientAsItsInheritedContactsAreCurrentlyUsed,
              )}
              placement="bottom-start"
              errorIconClassName="header-tag-parent-error-icon"
            />
          )}
        </MuiBox>
      </PermissionWrapper>
    );
  };

  return (
    <CreateClientSemiHeaderWrapper>
      <AccordionHeader
        title={
          <AccordionHeaderText
            label={fetchComponentTitle(
              keyClientDetailsSiteCoreKeys.keyClientDetailsHeader,
            )}
          />
        }
        showExpandCollapseIcon={false}
      >
        <MuiBox className="semi-header-container">
          <MuiGrid
            className="header-parent-client-container"
            size={{ sm: 3, xs: 12 }}
          >
            <MuiTypography variant="h3" className="header-label">
              {fetchLabel(
                keyClientDetailsSiteCoreKeys.keyClientDetailsHeader,
                keyClientDetailsSiteCoreKeys.client,
              )}
            </MuiTypography>
            {isEmpty(clientCode) ? (
              <MuiTypography variant="h4Bold" className="header-label-value">
                {EMPTY_VALUE_DASH}
              </MuiTypography>
            ) : (
              <>
                <MuiEllipseLabel
                  labelTypoVariant={"h4Bold"}
                  text={clientName}
                  labelTypoClassName={`header-label-value`}
                />

                <MuiTypography variant="h4Bold" className="header-label-value">
                  {clientCode}
                </MuiTypography>
              </>
            )}
          </MuiGrid>
          {!isRetailClient(clientData.clientTypeId) && (
            <>
              <MuiGrid
                className="header-parent-client-container"
                size={{ sm: 6, xs: 12 }}
              >
                <MuiTypography variant="h3" className="header-label">
                  {fetchLabel(
                    keyClientDetailsSiteCoreKeys.keyClientDetailsHeader,
                    keyClientDetailsSiteCoreKeys.parentClient,
                  )}
                </MuiTypography>
                {isEmpty(parentClientCode) ? (
                  <MuiTypography
                    variant="h4Bold"
                    className="header-label-value"
                  >
                    {EMPTY_VALUE_DASH}
                  </MuiTypography>
                ) : (
                  <>
                    <MuiEllipseLabel
                      labelTypoVariant={"h4Bold"}
                      text={parentClient}
                      labelTypoClassName={`header-label-value`}
                    />

                    <MuiTypography
                      variant="h4Bold"
                      className="header-label-value"
                    >
                      {parentClientCode}
                    </MuiTypography>
                  </>
                )}
                {isAddButtonVisible &&
                  isNotEmpty(clientCode) &&
                  !isRetailClient(clientData.clientTypeId) &&
                  renderParentClientButton()}
              </MuiGrid>

              {/* <MuiGrid
                container
                className="header-tag-parent-container"
                size={{ sm: 4, xs: 12 }}
              >
                {isEditable
                  ? isNotEmpty(clientCode) && renderParentClientButton()
                  : null}
              </MuiGrid> */}
            </>
          )}
        </MuiBox>
      </AccordionHeader>
    </CreateClientSemiHeaderWrapper>
  );
};
